// Sidebar.js - Updated with new design
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  Switch,
  Stack,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LocalShipping,
  Search,
  History,
  Email,
  Numbers,
  AdminPanelSettings,
  Brightness4,
  Brightness7,
  Logout,
  AccountCircle,
  Inbox,
  Send,
  Campaign,
  Layers,
  Visibility,
  Settings
} from '@mui/icons-material';

const Sidebar = ({ 
  drawerWidth, 
  mobileOpen, 
  handleDrawerToggle, 
  activeTab, 
  setActiveTab,
  searchHistory,
  darkMode,
  onToggleDarkMode,
  isAdminLoggedIn = false,
  isAuthenticatedUser = false,
  user = null,
  logout = () => {},
  usageRemaining = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = [
    { text: 'Search Carrier', icon: <Search />, tab: 'search' },
    { 
      text: 'Email Studio', 
      icon: <Email />, 
      tab: 'email-header',
      isHeader: true,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    { text: 'Compose Email', icon: <Send />, tab: 'email-compose', indent: true },
    { text: 'Campaign Planner', icon: <Campaign />, tab: 'email-campaign', indent: true },
    { text: 'Templates', icon: <Layers />, tab: 'email-templates', indent: true },
    { text: 'Unified Inbox', icon: <Inbox />, tab: 'email-inbox', indent: true },
    { text: 'Seen Emails', icon: <Visibility />, tab: 'email-seen', indent: true },
    { text: 'Accounts', icon: <Settings />, tab: 'email-accounts', indent: true },
    { text: 'Activity', icon: <History />, tab: 'activity' },
    { text: 'Search History', icon: <History />, tab: 'history', badge: searchHistory.length },
    { text: 'Admin Portal', icon: <AdminPanelSettings />, tab: 'admin', admin: true }
  ];

  const searchTypes = [
    { text: 'MC Number Search', icon: <Numbers />, type: 'mc', desc: 'Motor Carrier Number' },
    { text: 'DOT Number Search', icon: <LocalShipping />, type: 'dot', desc: 'USDOT Number' },
    { text: 'Email Search', icon: <Email />, type: 'email', desc: 'Registered Email' }
  ];

  const drawer = (
    <Box sx={{ 
      overflow: 'auto', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: darkMode 
        ? 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)'
        : 'linear-gradient(180deg, #ffffff 0%, #f8f9ff 100%)',
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        p: isMobile ? 2 : 3, 
        textAlign: 'center', 
        borderBottom: '1px solid', 
        borderColor: 'divider',
        background: darkMode
          ? 'linear-gradient(135deg, rgba(37,99,235,0.1) 0%, rgba(5,150,105,0.1) 100%)'
          : 'linear-gradient(135deg, rgba(37,99,235,0.05) 0%, rgba(5,150,105,0.05) 100%)',
      }}>
        <Box sx={{
          width: isMobile ? 50 : 60,
          height: isMobile ? 50 : 60,
          background: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 12px',
          color: 'white',
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: 'bold',
          boxShadow: '0 8px 24px rgba(37,99,235,0.3)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05) rotate(5deg)',
          }
        }}>
          CFP
        </Box>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight="bold" color="text.primary">
          Carrier Finder Pro
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Professional Search Tool
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ px: isMobile ? 1.5 : 2, py: 2, flex: 1 }}>
        {menuItems
          .filter((item) => !item.admin || isAdminLoggedIn)
          .map((item) => {
            const isSelected = activeTab === item.tab;
            const isEmailTab = item.tab && item.tab.startsWith('email-');
            const isActiveEmailSection = activeTab && activeTab.startsWith('email-');
            
            // Header item for Email Studio
            if (item.isHeader) {
              return (
                <Box key={item.text} sx={{ mb: 1, mt: 2, position: 'relative' }}>
                  <Box
                    sx={{
                      background: item.gradient,
                      borderRadius: 2,
                      p: 1.5,
                      mb: 1,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                        animation: 'shimmer 3s infinite',
                      },
                      '@keyframes shimmer': {
                        '0%': { left: '-100%' },
                        '100%': { left: '100%' },
                      }
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ color: '#fff', display: 'flex' }}>{item.icon}</Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontWeight: 800,
                          color: '#fff',
                          letterSpacing: '0.5px',
                          fontSize: '0.85rem',
                        }}
                      >
                        {item.text.toUpperCase()}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              );
            }
            
            return (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5, ml: item.indent ? 2 : 0 }}>
            <ListItemButton
              selected={isSelected}
              onClick={() => {
                setActiveTab(item.tab);
                if (isMobile) handleDrawerToggle();
              }}
              sx={{
                borderRadius: 1.5,
                py: isMobile ? 1.2 : 1,
                px: isMobile ? 1.5 : 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateX(4px)',
                  boxShadow: item.indent 
                    ? '0 2px 8px rgba(102, 126, 234, 0.15)'
                    : '0 2px 8px rgba(37, 99, 235, 0.15)',
                },
                ...(item.indent && {
                  borderLeft: '3px solid transparent',
                  borderLeftColor: isSelected ? '#667eea' : 'transparent',
                  '&:hover': {
                    borderLeftColor: '#8b7bd8',
                  }
                }),
                '&.Mui-selected': {
                  background: item.indent 
                    ? 'linear-gradient(90deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)'
                    : darkMode 
                      ? 'rgba(37, 99, 235, 0.16)' 
                      : 'rgba(37, 99, 235, 0.08)',
                  border: item.indent ? 'none' : '1px solid',
                  borderColor: item.indent ? 'transparent' : 'primary.main',
                  boxShadow: item.indent
                    ? '0 4px 12px rgba(102, 126, 234, 0.2)'
                    : '0 2px 8px rgba(37, 99, 235, 0.2)',
                  '&:hover': {
                    background: item.indent
                      ? 'linear-gradient(90deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%)'
                      : darkMode 
                        ? 'rgba(37, 99, 235, 0.24)' 
                        : 'rgba(37, 99, 235, 0.12)',
                  },
                  '&::before': item.indent ? {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '3px',
                    height: '60%',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '0 2px 2px 0',
                  } : {}
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: isSelected 
                  ? (item.indent ? '#667eea' : 'primary.main')
                  : 'text.secondary',
                minWidth: isMobile ? 35 : 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontWeight: isSelected ? 700 : (item.indent ? 500 : 400),
                  color: isSelected 
                    ? (item.indent ? '#667eea' : 'primary.main')
                    : 'text.primary',
                  fontSize: isMobile ? '0.875rem' : (item.indent ? '0.9rem' : 'inherit')
                }}
              />
              {item.badge > 0 && (
                <Chip 
                  label={item.badge} 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 600,
                    height: 20,
                    minWidth: 20
                  }} 
                />
              )}
            </ListItemButton>
          </ListItem>
          );
        })}
      </List>

      <Divider sx={{ my: 1 }} />

      {/* Search Types */}
      <Box sx={{ px: isMobile ? 1.5 : 3, pb: isMobile ? 1.5 : 2 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 700, display: 'block' }}>
          SEARCH TYPES
        </Typography>
        <Box sx={{ display: 'grid', gap: 1 }}>
          {searchTypes.map((item) => (
            <Box
              key={item.type}
              sx={{
                p: isMobile ? 1 : 2,
                backgroundColor: darkMode ? 'grey.800' : 'grey.50',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: darkMode ? 'grey.700' : 'grey.100',
                  transform: 'translateX(4px)'
                }
              }}
              onClick={() => {
                setActiveTab('search');
                if (isMobile) handleDrawerToggle();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5 }}>
                <Box sx={{ 
                  background: 
                    item.type === 'mc' ? 'rgba(37, 99, 235, 0.1)' : 
                    item.type === 'dot' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                  borderRadius: 1,
                  p: isMobile ? 0.75 : 1,
                  color: 
                    item.type === 'mc' ? 'primary.main' : 
                    item.type === 'dot' ? 'secondary.main' : 'warning.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: isMobile ? 32 : 40,
                  minHeight: isMobile ? 32 : 40
                }}>
                  {item.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" fontWeight="600" display="block">
                    {item.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ px: isMobile ? 1.5 : 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ fontWeight: 700, display: 'block' }}>
          QUICK STATS
        </Typography>
        <Box sx={{ display: 'grid', gap: 0.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Total Searches:</Typography>
            <Typography variant="caption" fontWeight="600" color="primary.main">
              {searchHistory.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Today:</Typography>
            <Typography variant="caption" fontWeight="600" color="secondary.main">
              {searchHistory.filter(s => new Date(s.timestamp).toDateString() === new Date().toDateString()).length}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* User Info & Login Section */}
      {isAuthenticatedUser ? (
        <Box sx={{ p: isMobile ? 1.5 : 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box
            onClick={() => {
              setActiveTab('profile');
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              p: 1,
              border: '1px solid',
              borderColor: activeTab === 'profile' ? 'primary.main' : 'transparent',
              backgroundColor: activeTab === 'profile'
                ? (darkMode ? 'rgba(59,130,246,0.16)' : 'rgba(59,130,246,0.08)')
                : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(59,130,246,0.12)' : 'rgba(59,130,246,0.08)',
              }
            }}
          >
            <Box sx={{ 
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              flexShrink: 0
            }}>
              <AccountCircle sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="caption" fontWeight="700" display="block" noWrap>
                {user?.name || user?.email?.split('@')[0] || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
            </Box>
          </Box>
          
          {typeof usageRemaining === 'number' && (
            <Box sx={{ 
              p: 1,
              mb: 1.5,
              backgroundColor: darkMode ? 'grey.800' : 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {usageRemaining === -1 ? 'Account Status' : 'Remaining Searches'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight="700" color={usageRemaining !== 0 ? 'primary.main' : 'error.main'}>
                  {usageRemaining === -1 ? 'Unlimited' : usageRemaining}
                </Typography>
                <Chip 
                  size="small" 
                  label={usageRemaining === -1 ? 'Active' : (usageRemaining > 0 ? 'Available' : 'Limited')}
                  color={usageRemaining === -1 || usageRemaining > 0 ? 'success' : 'error'}
                  sx={{ height: 20 }}
                />
              </Box>
            </Box>
          )}

          <Box
            onClick={() => {
              logout();
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              p: isMobile ? 1 : 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'error.main',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)',
              }
            }}
          >
            <Logout sx={{ fontSize: isMobile ? '1rem' : '1.1rem', color: 'error.main' }} />
            <Typography variant="caption" fontWeight="600" color="error.main">
              Logout
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: isMobile ? 1.5 : 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box
            onClick={() => {
              setActiveTab('admin');
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              p: isMobile ? 1.5 : 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 1.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'primary.dark',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <AccountCircle sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
            <Typography variant="caption" fontWeight="700" textAlign="center">
              Sign In
            </Typography>
          </Box>
        </Box>
      )}

      {/* Dark Mode Toggle */}
      <Box sx={{ p: isMobile ? 1.5 : 2, borderTop: '1px solid', borderColor: 'divider', mt: 'auto' }}>
        <FormControlLabel
          control={
            <Switch
              checked={darkMode}
              onChange={onToggleDarkMode}
              icon={<Brightness7 sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />}
              checkedIcon={<Brightness4 sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />}
              size={isMobile ? 'small' : 'medium'}
            />
          }
          label={<Typography variant="caption" fontWeight="600">{darkMode ? 'Dark' : 'Light'}</Typography>}
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
